import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { truckService } from '../api/api';
import { AddInspectionDialog, DefectReportDialog, InspectionReportDialog } from './TruckInspectionLogs';
import { formatPortalDate } from '../utils/dateFormat';

function formatDateTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return formatPortalDate(d, { includeTime: true });
}

function labelType(type) {
  if (type === 'pre_trip') return 'Pre Trip';
  if (type === 'post_trip') return 'Post Trip';
  if (type === 'defect_report') return 'Defect Report';
  return type || '—';
}

function labelStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'pass') return 'Pass';
  if (s === 'fail') return 'Fail';
  if (s === 'needs_attention') return 'Needs Attention';
  return status || '—';
}

function statusChipProps(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'pass') return { label: 'Pass', color: 'success', variant: 'filled' };
  if (s === 'needs_attention') return { label: 'Needs Attention', color: 'warning', variant: 'filled' };
  if (s === 'fail') return { label: 'Fail', color: 'error', variant: 'filled' };
  return { label: labelStatus(status), color: 'default', variant: 'outlined' };
}

function rowSx(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'needs_attention') return { backgroundColor: 'rgba(255, 167, 38, 0.08)' };
  if (s === 'fail') return { backgroundColor: 'rgba(211, 47, 47, 0.08)' };
  return {};
}

function truckLabel(t) {
  if (!t) return '—';
  return t.name || [t.year, t.make, t.model].filter(Boolean).join(' ') || `Truck #${t.id}`;
}

function otherDriverLabel(inspection) {
  const details = inspection?.details || {};
  return details?.other_driver_name || details?.co_driver_name || '';
}

export default function DriversInspectionTable() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [sortBy, setSortBy] = useState('occurred_at');
  const [sortDir, setSortDir] = useState('desc');

  const [reportOpen, setReportOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedTruckId, setSelectedTruckId] = useState(null);

  const params = useMemo(() => ({
    page,
    pageSize,
    q: q || undefined,
    type: type === 'all' ? undefined : type,
    status: status === 'all' ? undefined : status,
    sortBy,
    sortDir,
  }), [page, pageSize, q, type, status, sortBy, sortDir]);

  const load = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError('');
      const res = await truckService.getAllInspections(params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to load inspections.');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const refreshQuietly = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };
    const interval = window.setInterval(refreshQuietly, 10_000);
    window.addEventListener('focus', refreshQuietly);
    window.addEventListener('dataUpdated', refreshQuietly);
    document.addEventListener('visibilitychange', refreshQuietly);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshQuietly);
      window.removeEventListener('dataUpdated', refreshQuietly);
      document.removeEventListener('visibilitychange', refreshQuietly);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.pageSize, params.q, params.type, params.status, params.sortBy, params.sortDir]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  const openReport = (wrapped) => {
    const inspection = wrapped?.inspection || wrapped || null;
    if (!inspection) return;
    setSelectedInspection(inspection);
    setSelectedTruckId(wrapped?.truck?.id || inspection.truck_id || null);
    setReportOpen(true);
  };

  const openEdit = (wrapped) => {
    const inspection = wrapped?.inspection || wrapped || null;
    if (!inspection) return;
    setReportOpen(false);
    setSelectedInspection(inspection);
    setSelectedTruckId(wrapped?.truck?.id || inspection.truck_id || null);
    setEditOpen(true);
  };

  return (
    <>
      <Paper sx={{ mt: { xs: 1.5, sm: 3 }, p: { xs: 1.25, sm: 2.5 }, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Submitted Inspections
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '150px 150px minmax(220px, 1fr) 160px 130px 140px' }, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={(e) => { setPage(0); setType(e.target.value); }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pre_trip">Pre Trip</MenuItem>
              <MenuItem value="post_trip">Post Trip</MenuItem>
              <MenuItem value="defect_report">Defect Reports</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => { setPage(0); setStatus(e.target.value); }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pass">Pass</MenuItem>
              <MenuItem value="fail">Fail</MenuItem>
              <MenuItem value="needs_attention">Needs Attention</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Search"
            value={q}
            onChange={(e) => { setPage(0); setQ(e.target.value); }}
            placeholder="Search truck / inspector / status"
            sx={{ minWidth: 0 }}
          />

          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={(e) => { setPage(0); setSortBy(e.target.value); }}>
              <MenuItem value="occurred_at">Date/Time</MenuItem>
              <MenuItem value="truck">Truck</MenuItem>
              <MenuItem value="type">Type</MenuItem>
              <MenuItem value="status">Status</MenuItem>
              <MenuItem value="odometer">Odometer</MenuItem>
              <MenuItem value="inspected_by">Inspected By</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Order</InputLabel>
            <Select value={sortDir} label="Order" onChange={(e) => { setPage(0); setSortDir(e.target.value); }}>
              <MenuItem value="desc">Newest / High</MenuItem>
              <MenuItem value="asc">Oldest / Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 0 }}>
            <InputLabel>Per Page</InputLabel>
            <Select value={pageSize} label="Per Page" onChange={(e) => { setPage(0); setPageSize(parseInt(String(e.target.value), 10)); }}>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={150}>150</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Paper sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Truck</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date/Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Odometer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Inspected By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading…</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                        No matching records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((wrapped) => {
                    const inspection = wrapped?.inspection || wrapped;
                    const truck = wrapped?.truck || null;
                    return (
                      <TableRow
                        key={inspection?.id}
                        hover
                        sx={{ cursor: 'pointer', ...rowSx(inspection?.status) }}
                        onClick={() => openReport(wrapped)}
                      >
                        <TableCell sx={{ fontWeight: 700 }}>
                          {truckLabel(truck)}
                          {truck?.license_plate ? (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {truck.license_plate}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{labelType(inspection?.inspection_type)}</TableCell>
                        <TableCell>{formatDateTime(inspection?.occurred_at)}</TableCell>
                        <TableCell>{inspection?.odometer ?? '—'}</TableCell>
                        <TableCell>
                          {inspection?.inspected_by || '—'}
                          {otherDriverLabel(inspection) ? (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              Other: {otherDriverLabel(inspection)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" {...statusChipProps(inspection?.status)} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button size="small" variant="outlined" onClick={() => openReport(wrapped)} sx={{ borderRadius: 2 }}>
                            View
                          </Button>
                          <Button size="small" variant="contained" onClick={() => openEdit(wrapped)} sx={{ borderRadius: 2, ml: 1 }}>
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="textSecondary">
            Page {page + 1} of {totalPages}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Previous
            </Button>
            <Button variant="outlined" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </Box>
        </Box>
      </Paper>

      <InspectionReportDialog
        open={reportOpen}
        onClose={() => { setReportOpen(false); setSelectedInspection(null); setSelectedTruckId(null); }}
        inspection={selectedInspection}
        onEdit={() => {
          setReportOpen(false);
          setEditOpen(true);
        }}
      />
      <AddInspectionDialog
        open={editOpen && selectedInspection?.inspection_type !== 'defect_report'}
        onClose={() => { setEditOpen(false); setSelectedInspection(null); setSelectedTruckId(null); }}
        truckId={selectedTruckId}
        existingInspection={selectedInspection}
        onUpdated={() => {
          setEditOpen(false);
          setSelectedInspection(null);
          setSelectedTruckId(null);
          load();
        }}
      />
      <DefectReportDialog
        open={editOpen && selectedInspection?.inspection_type === 'defect_report'}
        onClose={() => { setEditOpen(false); setSelectedInspection(null); setSelectedTruckId(null); }}
        truckId={selectedTruckId}
        existingInspection={selectedInspection}
        onSaved={() => {
          setEditOpen(false);
          setSelectedInspection(null);
          setSelectedTruckId(null);
          load();
        }}
      />
    </>
  );
}
